<div class="udex-grid udex-mx-auto udex-py-56">
  <div class="block-container udex-mx-auto">
    @if (hasError()) {
      <udex-message-strip
        design="Negative"
        status-type="None"
        hide-close-button
        hide-icon
        class="text-grey udex-mb-20">
        <h3 class="heading__regular--xs udex-mb-8">
          {{ 'registration.error.somethingWrong' | cxTranslate }}
        </h3>
        @if (errorStatus) {
          <p class="body--m">
            {{ ((errorStatus === 400) ? 'registration.error.validation' : 'registration.error.generic') | cxTranslate }}
          </p>
        }
      </udex-message-strip>
    }

    <h1 class="heading__regular--xs udex-mb-20">
      {{ 'registration.heading' | cxTranslate }}
    </h1>

    <p class="body--m text-grey udex-mb-20">
      {{'registration.subheading' | cxTranslate}}
    </p>

    <form [formGroup]="form">
      <!--  Country -->
      <udex-country-selector
        required
        id="country-selector"
        height="16rem"
        [label]="'registration.placeholder.countryCode' | cxTranslate"
        [preselectedCountry]="currentCountryIso"
        (selection-change)="onCountryChanged($event)"
        class="d-block udex-mb-24">
      </udex-country-selector>

      <!--  Company name  -->
      <div class="position-relative udex-mb-24">
        <udex-text-field
          required
          type="text"
          [valueState]="getErrorStatus('company')"
          [supportingText]="isMaxLengthError('company') ? (getErrorMessage('company') | cxTranslate: { maxLength: getMaxLength('company') }) : (getErrorMessage('company') | cxTranslate)"
          [label]="'registration.placeholder.company' | cxTranslate"
          [value]="getFormControl('company').value"
          (input)="onInputChange($event, 'company')"
          (keydown.enter)="setShowErrorMessages(true)"
          tooltipPlacement="bottom-end"
          #companyName>
        </udex-text-field>
        <div class="registration-form-tooltip">
          <udex-button
            class="text-grey udex-me-2"
            #UDExCompanyTooltipOpener
            size="Medium"
            icon="sap-icon://message-information"
            design="Tertiary"
            (mouseover)="showCompanyTooltip()"
            (mouseout)="hideCompanyTooltip()"
            icon-only
            accessible-name="companyNameTooltipButton"
            accessible-role="Link">
          </udex-button>
          <udex-tooltip #UDExCompanyTooltip placement="Right" accessible-name="companyNameTooltip"
                        accessible-role="Dialog">
            <span class="text-grey body--s">{{ 'registration.tooltip.company' | cxTranslate }}</span>
          </udex-tooltip>
        </div>
      </div>

      <!--  Address  -->
      <div class="position-relative udex-mb-24">
        <udex-text-field
          required
          [valueState]="getErrorStatus('street')"
          [supportingText]="isMaxLengthError('street') ? (getErrorMessage('street') | cxTranslate: { maxLength: getMaxLength('street') }) : (getErrorMessage('street') | cxTranslate)"
          (keydown.enter)="setShowErrorMessages(true)"
          (input)="onInputChange($event, 'street')"
          [label]="'registration.placeholder.street' | cxTranslate"
          [value]="getFormControl('street').value"
          #streetAddress
        ></udex-text-field>
        <div class="registration-form-tooltip">
          <udex-button
            class="text-grey udex-me-2"
            #UDExAddressTooltipOpener
            size="Medium"
            icon="sap-icon://message-information"
            design="Tertiary" (mouseover)="showAddressTooltip()" (mouseout)="hideAddressTooltip()"
            icon-only
            accessible-name="companyAddressTooltipButton"
            accessible-role="Link"
          ></udex-button>
          <udex-tooltip #UDExAddressTooltip placement=" Right
          " accessible-name="companyAddressTooltip"
                        accessible-role="Dialog">
            <span class="text-grey body--s">{{ 'registration.tooltip.street' | cxTranslate }}</span>
          </udex-tooltip>
        </div>
      </div>

      <!--  Address Line 2  -->
      <udex-text-field
        class="d-block udex-mb-24"
        [label]="'registration.placeholder.addressLine2' | cxTranslate"
        [value]="getFormControl('addressLine2').value"
        [valueState]="getErrorStatus('addressLine2')"
        [supportingText]="isMaxLengthError('addressLine2') ? (getErrorMessage('addressLine2') | cxTranslate: { maxLength: getMaxLength('addressLine2') }) : (getErrorMessage('addressLine2') | cxTranslate)"
        (keydown.enter)="setShowErrorMessages(true)"
        (input)="onInputChange($event, 'addressLine2')">
      </udex-text-field>

      <!--  City  -->
      <udex-text-field
        required
        class="d-block udex-mb-24"
        [label]="'registration.placeholder.city' | cxTranslate"
        [value]="getFormControl('city').value"
        [valueState]="getErrorStatus('city')"
        [supportingText]="isMaxLengthError('city') ? (getErrorMessage('city') | cxTranslate: { maxLength: getMaxLength('city') }) : (getErrorMessage('city') | cxTranslate)"
        (keydown.enter)="setShowErrorMessages(true)"
        (input)="onInputChange($event, 'city')">
      </udex-text-field>

      <!--  RegionCode & PostCode  -->
      <div class="d-flex justify-content-between udex-mb-24">
        @if (isRequired(form.get('regionCode'))) {
          <udex-select-box
            class="d-block medium-field"
            required
            #regionCode
            [supportingText]="getErrorMessage('regionCode') | cxTranslate"
            [label]="'registration.placeholder.regionCode' | cxTranslate"
            [disabled]="fetchingRegions"
            [valueState]="getErrorStatus('regionCode')"
            height="16rem"
            (selection-change)="onRegionCodeChanged($event)">
            @for (region of regions; track trackByRegion) {
              <udex-select-box-item
                [label]="region.name"
                [attr.data-value]="region.isocode"
                [selected]="getFormControl('regionCode').value === region.isocode">
              </udex-select-box-item>
            }
          </udex-select-box>
        }
        <udex-text-field
          required
          class="d-block"
          [ngClass]="{ 'medium-field': isRequired(form.get('regionCode')), 'large-field': !isRequired(form.get('regionCode')) }"
          [valueState]="getErrorStatus('postalCode')"
          [supportingText]="isMaxLengthError('postalCode') ? (getErrorMessage('postalCode') | cxTranslate: { maxLength: getMaxLength('postalCode') }) : (getErrorMessage('postalCode') | cxTranslate)"
          [label]="'registration.placeholder.postalCode' | cxTranslate"
          [value]="getFormControl('postalCode').value"
          (keydown.enter)="setShowErrorMessages(true)"
          (input)="onInputChange($event, 'postalCode')">
        </udex-text-field>
      </div>

      <!--  Industry  -->
      @if (industries$ | async; as industries) {
        <udex-select-box
          required
          class="d-block udex-mb-24"
          height="20rem"
          [label]="'registration.placeholder.lineOfBusiness' | cxTranslate"
          [valueState]="getErrorStatus('lineOfBusiness')"
          [supportingText]="getErrorMessage('lineOfBusiness') | cxTranslate"
          (selection-change)="onIndustryChanged($event)">
          @for (industry of industries; track trackByIndustry) {
            <udex-select-box-item
              [label]="industry.description"
              [attr.data-value]="industry.code"
              [selected]="getFormControl('lineOfBusiness').value === industry.description">
            </udex-select-box-item>
          }
        </udex-select-box>
      }

      <p class="body--xs text-blue-gray udex-mb-20">{{ 'registration.info' | cxTranslate }}</p>

      <div class="d-flex">
        <udex-button
          id="backButton"
          size="Large"
          design="Secondary"
          [disabled]="inProgress"
          (click)="onCancel()">
          {{ 'common.back' | cxTranslate }}
        </udex-button>

        <udex-button
          id="continueButton"
          class="udex-ms-16"
          size="Large"
          design="Primary"
          type="submit"
          [disabled]="inProgress || fetchingRegions"
          (click)="onSubmit()">
          @if (isDone) {
            <em class="ds-icon ds-icon--checked"></em>
          } {{'common.continue' | cxTranslate}}
        </udex-button>
      </div>

    </form>
  </div>
</div>
